import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import PUBLIC from "@/data/gatePublic.json";
import { api, useAuth } from "@/lib/auth";
import { Reveal } from "./Reveal";
import { scrollToId } from "@/lib/scroll";
import EnergyMap from "@/components/app/EnergyMap";
import PlayAllButton from "@/components/app/PlayAllButton";

const SAMPLE_GATE = 4;
const PRACTICE = /protocol|practice|ritual|activation/i;

const LockedGuide = ({ i, signedIn }) => {
  const meta = PUBLIC.gates[i] ?? {};
  return (
    <section data-testid="mastery-guide-locked" className="py-24 sm:py-32 border-t border-[#D9B47A]/15">
      <Reveal>
        <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">The Mastery Guide · For Initiates</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl mt-6 leading-[1.02]">{(meta.title ?? "").replace(/ Mastery Guide$/, "")}</h2>
        <p className="font-display italic text-xl sm:text-2xl text-[#D9B47A] mt-4">{meta.subtitle}</p>
      </Reveal>
      <div className="relative mt-14 border border-[#D9B47A]/20 overflow-hidden">
        <ol className="p-8 sm:p-12 grid sm:grid-cols-2 gap-x-10 gap-y-5 select-none blur-[3px] opacity-60" aria-hidden="true">
          {(meta.sections ?? []).map((h, k) => (
            <li key={h} className="flex gap-4 font-display text-xl sm:text-2xl"><span className="font-tt-mono text-[12px] text-[#C2414F] pt-2">{String(k + 1).padStart(2, "0")}</span>{h}</li>
          ))}
        </ol>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0706]/40 via-[#0A0706]/85 to-[#0A0706] flex flex-col items-center justify-center text-center px-6">
          <span className="w-14 h-14 rounded-full border border-[#D9B47A]/60 flex items-center justify-center"><Lock className="w-5 h-5 text-[#D9B47A]" /></span>
          <p className="font-display text-3xl sm:text-4xl mt-6 max-w-xl">This ritual opens for <em className="text-[#D9B47A]">initiates.</em></p>
          <p className="mt-4 text-[#CFC2B6] max-w-md">Lifetime access to all 13 Mastery Guide rituals — the full protocols, 3-week rhythms, partnered practice and blessings.</p>
          <Link to="/initiate" data-testid="locked-become-initiate" className="mt-8 rounded-full bg-[#D9B47A] text-[#0A0706] px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors">
            Become an initiate · $297 AUD
          </Link>
          {!signedIn && <Link to="/signin" data-testid="locked-signin" className="mt-5 text-sm text-[#CFC2B6] underline hover:text-[#D9B47A]">Already an initiate? Sign in</Link>}
          <Link to="/gates/breathwave" className="mt-3 text-sm text-[#A8998C] hover:text-[#D9B47A]">Try the free Gate 5 Breathwave sample</Link>
        </div>
      </div>
    </section>
  );
};

const group = (items) => {
  const out = [];
  items.forEach((it) => {
    const last = out[out.length - 1];
    if (it.k === "li" && last?.k === "ul") last.items.push(it.t);
    else if (it.k === "li") out.push({ k: "ul", items: [it.t] });
    else out.push(it);
  });
  return out;
};

const Affirm = ({ t }) => (
  <p className="font-display italic text-2xl sm:text-3xl text-[#D9B47A] my-6">{t.replace(/^Affirm:\s*/, "")}</p>
);

const Item = ({ it }) => {
  if (it.k === "h3")
    return <h4 className="font-display text-2xl sm:text-3xl mt-12 mb-4 pt-6 border-t border-[#D9B47A]/20 text-[#F2E8DF]">{it.t}</h4>;
  if (it.k === "label") {
    if (/^With /.test(it.t)) return <p className="font-display italic text-xl text-[#D9B47A] mt-8">{it.t.replace(/ Goddess Ariel.*/, "")} — Goddess Ariel</p>;
    return <p className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#D9B47A] mt-7 mb-3">{it.t.replace(/:$/, "")}</p>;
  }
  if (it.k === "ul")
    return (
      <ul className="space-y-2.5 my-4">
        {it.items.map((t, k) =>
          /^Affirm:/.test(t) ? <li key={k} className="list-none"><Affirm t={t} /></li> : (
            <li key={k} className="flex gap-4 text-[16px] sm:text-[17px] text-[#DCCFC3] leading-relaxed">
              <span className="mt-[0.7em] w-1.5 h-1.5 rotate-45 bg-[#C2414F] shrink-0" />
              <span>{t}</span>
            </li>
          )
        )}
      </ul>
    );
  if (/^Affirm:/.test(it.t)) return <Affirm t={it.t} />;
  return <p className="text-[16px] sm:text-[18px] text-[#DCCFC3] leading-[1.8] my-4">{it.t}</p>;
};

const useActive = (ids) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-30% 0px -60% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [ids]);
  return active;
};

export default function MasteryGuide({ i }) {
  const { user } = useAuth();
  const [state, setState] = useState({ i: null, guide: null, locked: false });
  useEffect(() => {
    if (i !== SAMPLE_GATE && user === null) return undefined;
    let alive = true;
    api
      .get(`/guides/${i}`)
      .then((r) => alive && setState({ i, guide: r.data, locked: false }))
      .catch(() => alive && setState({ i, guide: null, locked: true }));
    return () => {
      alive = false;
    };
  }, [i, user]);
  const guide = state.i === i ? state.guide : null;
  const sections = Array.isArray(guide?.sections) ? guide.sections : [];
  const ids = sections.map((_, k) => `guide-${i}-${k}`);
  const active = useActive(ids);
  if (state.i === i && state.locked) return <LockedGuide i={i} signedIn={Boolean(user)} />;
  if (!sections.length)
    return <div data-testid="mastery-guide-loading" className="py-24 flex items-center gap-3 text-[#A8998C]"><Loader2 className="w-4 h-4 animate-spin" /> Opening the ritual…</div>;
  const label = (s) => (/^Welcome/.test(s.h) ? "Welcome" : s.h);

  return (
    <section data-testid="mastery-guide" className="py-24 sm:py-32 border-t border-[#D9B47A]/15">
      <Reveal>
        <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">The Mastery Guide{i === SAMPLE_GATE && !user?.initiate ? " · Free Sample" : ""}</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl mt-6 leading-[1.02]">{guide.title.replace(/ Mastery Guide$/, "")}</h2>
        <p className="font-display italic text-xl sm:text-2xl text-[#D9B47A] mt-4">{guide.subtitle}</p>
        {user?.initiate && <div className="mt-8"><PlayAllButton texts={sections.map((s) => `${s.h}. ${s.items.map((it) => it.t ?? "").join(" ")}`.replace(/\*\*/g, "").slice(0, 2900))} onIndex={(k) => scrollToId(ids[k])} testId="guide-play-all" /></div>}
      </Reveal>

      <div data-testid="guide-mobile-nav" className="lg:hidden mt-10 -mx-5 px-5 flex gap-2 overflow-x-auto pb-2" data-lenis-prevent>
        {sections.map((s, k) => (
          <button key={ids[k]} onClick={() => scrollToId(ids[k])} className="shrink-0 rounded-full border border-[#D9B47A]/30 px-4 py-2 text-sm text-[#CFC2B6]">
            {label(s)}
          </button>
        ))}
      </div>

      <div className="mt-14 grid lg:grid-cols-12 gap-12">
        <nav className="hidden lg:block lg:col-span-3">
          <ol className="sticky top-32 space-y-3">
            {sections.map((s, k) => (
              <li key={ids[k]}>
                <button
                  data-testid={`guide-nav-${k + 1}`}
                  onClick={() => scrollToId(ids[k])}
                  className={`flex gap-3 text-left text-sm transition-colors duration-300 ${active === ids[k] ? "text-[#F2E8DF]" : "text-[#8F8176] hover:text-[#CFC2B6]"}`}
                >
                  <span className={`font-tt-mono text-[11px] pt-0.5 ${active === ids[k] ? "text-[#C2414F]" : "text-[#D9B47A]"}`}>{String(k + 1).padStart(2, "0")}</span>
                  {label(s)}
                </button>
              </li>
            ))}
          </ol>
        </nav>
        <div className="lg:col-span-9 max-w-[760px]">
          {sections.map((s, k) => (
            <article key={ids[k]} id={ids[k]} data-testid={`guide-section-${k + 1}`} className="scroll-mt-28 pb-14">
              <Reveal y={20}>
                <div className="flex items-baseline gap-5">
                  <span className="font-tt-mono text-[12px] text-[#C2414F]">{String(k + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-3xl sm:text-4xl">{s.h}</h3>
                </div>
              </Reveal>
              <div className="mt-4">
                {group(s.items).map((it, n) => <Item key={n} it={it} />)}
              </div>
              {PRACTICE.test(s.h) && <EnergyMap text={s.items.map((it) => it.t ?? "").join(" ")} partner={/partner/i.test(s.h)} testId={`guide-energy-map-${k + 1}`} />}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
